import type { RespostaPayload } from './regras';

/**
 * Armazenamento no aparelho (IndexedDB): a fila de respostas ainda não enviadas e uma cópia da lista de pesquisas,
 * para o app funcionar sem internet. As respostas não têm nome nem contato de ninguém (só faixa etária, sexo e notas).
 */

export type ItemFila = {
  idLocal: string;
  usuarioId: string;
  pesquisaId: string;
  pesquisaTitulo: string;
  municipioNome: string;
  payload: RespostaPayload;
  criadaEm: string;
  /** pendente = espera envio; rejeitada = a API recusou (o motivo fica em `erro`) e a pessoa decide o que fazer. */
  estado: 'pendente' | 'rejeitada';
  erro?: string;
  tentativas: number;
};

const NOME = 'mapas-coleta';
const VERSAO = 1;
let aberto: Promise<IDBDatabase> | null = null;

function abrir(): Promise<IDBDatabase> {
  aberto ??= new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') return reject(new Error('Este navegador não permite guardar dados no aparelho.'));
    const req = indexedDB.open(NOME, VERSAO);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('fila')) db.createObjectStore('fila', { keyPath: 'idLocal' }).createIndex('usuario', 'usuarioId');
      if (!db.objectStoreNames.contains('cache')) db.createObjectStore('cache', { keyPath: 'chave' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      aberto = null; // permite tentar de novo
      reject(new Error('Não foi possível abrir o armazenamento do aparelho.'));
    };
  });
  return aberto;
}

async function executar<T>(loja: 'fila' | 'cache', modo: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await abrir();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(loja, modo);
    const r = fn(tx.objectStore(loja));
    tx.oncomplete = () => resolve(r.result);
    tx.onerror = () => reject(tx.error ?? new Error('Falha ao gravar no aparelho.'));
    tx.onabort = () => reject(tx.error ?? new Error('Gravação cancelada: o aparelho pode estar sem espaço.'));
  });
}

export const filaListar = async (usuarioId: string): Promise<ItemFila[]> =>
  (await executar<ItemFila[]>('fila', 'readonly', (s) => s.index('usuario').getAll(usuarioId))).sort((a, b) => a.criadaEm.localeCompare(b.criadaEm));
export const filaGravar = (item: ItemFila) => executar('fila', 'readwrite', (s) => s.put(item));
export const filaRemover = (idLocal: string) => executar('fila', 'readwrite', (s) => s.delete(idLocal));

export async function cacheLer<T>(chave: string): Promise<{ salvoEm: string; dados: T } | null> {
  const r = await executar<{ chave: string; salvoEm: string; dados: T } | undefined>('cache', 'readonly', (s) => s.get(chave));
  return r ? { salvoEm: r.salvoEm, dados: r.dados } : null;
}
export const cacheGravar = <T>(chave: string, dados: T) => executar('cache', 'readwrite', (s) => s.put({ chave, salvoEm: new Date().toISOString(), dados }));
